import { CircularProgress, Grid } from '@material-ui/core';
import { Button, Dialog, Icons, Typography } from '@nutrien/cxp-components';
import React, { useEffect, useState } from 'react';

import { ISignAndStampData } from '../../models/models';
import { isJestTest } from '../../test-helpers/isJestTest';
import { useFileStorage } from '../../utilities/useFileStorage';
import { useOnlineStatus } from '../../utilities/useOnlineStatus';
import { XML_HEADER } from '../../utilities/constants';
import useStyles from './SignatureAndStamp.styles';

interface Props {
  data: ISignAndStampData;
  onDelete?: (file: ISignAndStampData) => void;
  readonly?: boolean;
  isSign?: boolean;
  isStamp?: boolean;
}

const SignatureAndStampView: React.FC<Props> = ({ data, onDelete, isSign, isStamp }) => {
  const { fileUUID, updatedName, fileData }: ISignAndStampData = data;
  const online = useOnlineStatus();
  const classes = useStyles();
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const { getAttachmentFromIndexDB, retryFileDownload } = useFileStorage();
  const [imageURL, setImageURL] = useState('');
  const [fileLoading, setFileLoading] = useState<boolean>(false);
  const imgRef = React.createRef<HTMLImageElement>();

  const getS3URL = async (fileId: string) => {
    let blobFile: Blob = (await getAttachmentFromIndexDB(fileId)) as Blob;
    if (online && !isJestTest() && (!blobFile || blobFile.type === XML_HEADER) && data.filePath) {
      setFileLoading(true);
      blobFile = await retryFileDownload(data.filePath, fileId);
      setFileLoading(false);
    }
    const url = blobFile ? URL.createObjectURL(blobFile) : '';
    setImageURL(url);
    return url;
  };

  useEffect(() => {
    if (fileData) {
      const url = URL.createObjectURL(fileData);
      setImageURL(url);
    } else if (fileUUID) {
      getS3URL(fileUUID);
    }
  }, [fileUUID, fileData]);

  const removeSignImage = async () => {
    setDeleteConfirm(true);
  };

  const handleDelete = (fileToDelete: ISignAndStampData) => {
    if (onDelete && fileToDelete) onDelete(fileToDelete);
    setDeleteConfirm(false);
  };

  return (
    <>
      {isSign && (
        <Grid>
          <Grid item xs={12} className={classes.clearSignButton}>
            <Typography color="error" className="clearSignText" onClick={removeSignImage}>
              Clear Sign
            </Typography>
          </Grid>
          <Grid className={classes.signCenter} item xs={3}>
            <Grid item xs={2} className={classes.stampImagePreview}>
              <Grid className={`${classes.signImageDiv} ${fileLoading && classes.loadingSign}`}>
                {!fileLoading ? (
                  <img
                    src={imageURL}
                    ref={imgRef}
                    alt="selected upload"
                    className={classes.signImage}
                  />
                ) : (
                  <CircularProgress />
                )}
              </Grid>
            </Grid>
          </Grid>
          {deleteConfirm && (
            <Dialog
              onClose={() => setDeleteConfirm(false)}
              open={deleteConfirm}
              titleText="Delete File"
              backButton={false}
              dialogActions={
                <Grid
                  container
                  justifyContent="flex-end"
                  alignItems="center"
                  className={classes.deleteConfirmGrid}
                >
                  <Grid item>
                    <Button
                      noMinHeight
                      color="error"
                      variant="contained"
                      onClick={() => {
                        setDeleteConfirm(false);
                        handleDelete(data);
                      }}
                    >
                      Yes, Delete
                    </Button>
                  </Grid>
                </Grid>
              }
              maxWidth="xs"
              noMinHeight
              data-testid="signDialogId"
            >
              <Grid container className={classes.deleteConfirmContentGrid}>
                <Grid item container xs={12} alignItems="center">
                  <Typography>{`Are you sure want to delete "${updatedName}"?`}</Typography>
                </Grid>
              </Grid>
            </Dialog>
          )}
        </Grid>
      )}
      {isStamp && (
        <Grid xs={12} item container className={classes.stampUploadContainer}>
          <Grid item xs={3}>
            <Grid item xs={2} className={classes.stampImagePreview}>
              <Grid item justifyContent="flex-end" className={classes.xfeatherOuterCircleStamp}>
                <Grid className={classes.xfeatherInnerCircleStamp}>
                  <Icons.XFeather
                    color="primary"
                    height="23px"
                    width="22px"
                    onClick={removeSignImage}
                    data-testid="removeIcon"
                  />
                </Grid>
              </Grid>
              <Grid className={`${classes.stampImageDiv} ${fileLoading && classes.loadingStamp}`}>
                {!fileLoading ? (
                  <>
                    <img src={imageURL} alt="selected upload" className={classes.stampImage} />
                    <Typography className={classes.imageName}>{updatedName}</Typography>
                  </>
                ) : (
                  <CircularProgress />
                )}
              </Grid>
            </Grid>
          </Grid>
          {deleteConfirm && (
            <Dialog
              onClose={() => setDeleteConfirm(false)}
              open={deleteConfirm}
              titleText="Delete File"
              backButton={false}
              dialogActions={
                <Grid
                  container
                  justifyContent="flex-end"
                  alignItems="center"
                  className={classes.deleteConfirmGrid}
                >
                  <Grid item>
                    <Button
                      noMinHeight
                      color="error"
                      variant="contained"
                      onClick={() => {
                        setDeleteConfirm(false);
                        handleDelete(data);
                      }}
                    >
                      Yes, Delete
                    </Button>
                  </Grid>
                </Grid>
              }
              maxWidth="xs"
              noMinHeight
              data-testid="stampDialogId"
            >
              <Grid container className={classes.deleteConfirmContentGrid}>
                <Grid item container xs={12} alignItems="center">
                  <Typography>{`Are you sure want to delete "${updatedName}"?`}</Typography>
                </Grid>
              </Grid>
            </Dialog>
          )}
        </Grid>
      )}
    </>
  );
};

export default SignatureAndStampView;
